import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, TextField, TabbedComponent, ColorPickerField } from '@dopomogai/ui';
import { PresentationTheme } from '@dopomogai/types';
import { Palette, Settings as SettingsIcon } from 'lucide-react';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: PresentationTheme;
  onThemeUpdate: (newTheme: PresentationTheme) => void;
  presentationTitle: string;
  onTitleUpdate: (newTitle: string) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  theme,
  onThemeUpdate,
  presentationTitle,
  onTitleUpdate,
}) => {
  const { t } = useTranslation(['settings', 'common']);
  const [localTitle, setLocalTitle] = useState(presentationTitle);
  const [localTheme, setLocalTheme] = useState(theme);

  useEffect(() => {
    if (isOpen) {
      setLocalTitle(presentationTitle);
      setLocalTheme(theme);
    }
  }, [isOpen, presentationTitle, theme]);

  const handleSave = () => {
    onTitleUpdate(localTitle);
    onThemeUpdate(localTheme);
    onClose();
  };

  const updateThemeColor = (key: keyof PresentationTheme['colors'], value: string) => {
    setLocalTheme(prev => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  };

  const tabs = [
    {
      id: 'general',
      label: t('tabs.general', 'General'),
      icon: <SettingsIcon size={16} />,
      content: (
        <div className="p-4 space-y-4">
          <TextField
            label={t('general.presentationTitleLabel', 'Presentation Title')}
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
          />
        </div>
      )
    },
    {
      id: 'theme',
      label: t('tabs.theme', 'Theme'),
      icon: <Palette size={16} />,
      content: (
        <div className="p-4 space-y-6">
            <h3 className="text-lg font-semibold text-slate-800">{t('theme.colorsTitle', 'Color Palette')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {Object.entries(localTheme.colors).map(([key, value]) => (
                    <ColorPickerField
                        key={key}
                        label={t(`theme.colors.${key}`, key.charAt(0).toUpperCase() + key.slice(1))}
                        value={value}
                        onChange={(color) => updateThemeColor(key as keyof PresentationTheme['colors'], color)}
                        portal={false} // THE FIX for positioning
                    />
                ))}
            </div>
        </div>
      )
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', 'Presentation Settings')}
      size="3xl" // THE FIX for width
      footer={
        <>
          <Button variant="outline" onClick={onClose} text={t('common:cancel', 'Cancel')} />
          <Button variant="primary" onClick={handleSave} text={t('common:save_changes', 'Save Changes')} />
        </>
      }
    >
      <TabbedComponent
          tabs={tabs}
          orientation="vertical"
          applyNeobrutalistEffectsToTabs={false}
      />
    </Modal>
  );
};